import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Yup from "yup";
import { Form } from "@unform/web";
import { CircularProgress, Switch } from "@mui/material";
import { Link } from "react-router-dom";
import intl from "react-intl-universal";
import { TbCheck, TbCopy, TbQrcode, TbRefresh } from "react-icons/tb";
import { RiWhatsappLine } from "react-icons/ri";
import QRCode from "react-qr-code";
import { InputMoney } from "../../../../Components/Atoms/InputMoney";
import { createPixQrCharge } from "../../../../Hooks/UsePix";
import { usePixKeys } from "../../../../Hooks/usePixKeys";
import { useToast } from "../../../../Hooks/toast";
import { useProgressAlert } from "../../../../Hooks/useProgressAlert";
import {
  convertToCents,
  formatCurrency,
  stringToDecimal,
} from "../../../../Utils/formatters";
import getValidationErrors from "../../../../Utils/getValidationErrors";
import {
  formatPixKeyValue,
  getPixKeyTypeMeta,
  isSelectablePixKey,
} from "../PixKeys/constants";
import { PixKeySelect } from "../PixKeys/PixKeySelect";
import { PixQrReceiveStepper } from "./PixQrReceiveStepper";

export const PIX_QR_RECEIVE_FORM_ID = "pix-qr-receive-form";

const QR_TYPES = {
  DYNAMIC: "dynamic",
  STATIC: "static",
};

const tomorrowIsoDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatIsoDatePtBr = (iso) => {
  if (!iso || iso.length < 10) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const QrTypeOption = ({ value, selected, onSelect, title, hint, icon: Icon, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onSelect(value)}
    aria-pressed={selected}
    className={`flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition ${
      selected
        ? "border-[#e7a73f] bg-[#e7a73f]/10 ring-1 ring-[#e7a73f]/40"
        : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/80"
    } disabled:cursor-not-allowed disabled:opacity-60`}
  >
    <span
      className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
        selected ? "border-[#e7a73f] bg-[#e7a73f]" : "border-gray-300 bg-white"
      }`}
      aria-hidden
    >
      {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
    </span>
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-brand-cream/60">
      <Icon className="h-3.5 w-3.5 text-brand-dark" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold leading-tight text-brand-dark">{title}</span>
      <span className="mt-0.5 block text-[11px] leading-snug text-gray-500 line-clamp-2">{hint}</span>
    </span>
  </button>
);

export const PixQrReceiveContent = forwardRef(function PixQrReceiveContent(
  {
    isActive = true,
    footerInModal = false,
    formId = PIX_QR_RECEIVE_FORM_ID,
    onStepChange,
  },
  ref
) {
  const progress = useProgressAlert();
  const { addToast } = useToast();
  const { keys, loading: keysLoading, error: keysError, reload: reloadKeys } = usePixKeys();
  const linkRef = useRef(null);
  const formRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkValue, setLinkValue] = useState(0);
  const [dueDate, setDueDate] = useState(tomorrowIsoDate);
  const [reusable, setReusable] = useState(false);
  const [qrType, setQrType] = useState(QR_TYPES.DYNAMIC);
  const [selectedKeyId, setSelectedKeyId] = useState("");
  const [qrState, setQrState] = useState(null);
  const [whatsAppUrl, setWhatsAppUrl] = useState("");

  const isStatic = qrType === QR_TYPES.STATIC;

  const selectableKeys = useMemo(
    () => (Array.isArray(keys) ? keys.filter(isSelectablePixKey) : []),
    [keys]
  );

  const selectedKey = useMemo(
    () => selectableKeys.find((item) => item.id === selectedKeyId) || null,
    [selectableKeys, selectedKeyId]
  );

  const canContinue =
    Boolean(selectedKey?.value) &&
    !keysLoading &&
    (isStatic || Boolean(dueDate));

  useEffect(() => {
    if (!selectableKeys.length) {
      setSelectedKeyId("");
      return;
    }

    setSelectedKeyId((prev) => {
      if (prev && selectableKeys.some((item) => item.id === prev)) {
        return prev;
      }
      const primary = selectableKeys.find((item) => item.isPrimary);
      return (primary || selectableKeys[0]).id;
    });
  }, [selectableKeys]);

  const resetStates = useCallback(() => {
    setLinkValue(0);
    setDueDate(tomorrowIsoDate());
    setReusable(false);
    setQrType(QR_TYPES.DYNAMIC);
    setStep(0);
    setWhatsAppUrl("");
    setQrState(null);
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    formRef.current?.reset();
    const primary = selectableKeys.find((item) => item.isPrimary);
    setSelectedKeyId((primary || selectableKeys[0])?.id || "");
  }, [selectableKeys]);

  useEffect(() => {
    if (!isActive) {
      resetStates();
    }
  }, [isActive, resetStates]);

  useEffect(() => {
    onStepChange?.({ step, isSubmitting, canContinue });
  }, [step, isSubmitting, canContinue, onStepChange]);

  const copyToClipboard = useCallback(async () => {
    try {
      if (qrState?.qrcode) {
        await navigator.clipboard.writeText(qrState.qrcode);
      } else if (linkRef.current) {
        linkRef.current.select();
        document.execCommand("copy");
      }
      addToast({
        title: intl.get("pix.receive.copiedTitle"),
        type: "success",
        description: intl.get("pix.receive.copiedDescription"),
      });
    } catch (err) {
      console.log(err);
      addToast({
        title: intl.get("pix.receive.copyErrorTitle"),
        type: "error",
        description: intl.get("pix.receive.copyErrorDescription"),
      });
    }
  }, [addToast, qrState?.qrcode]);

  const handleConfirmQrCodeCreation = useCallback(async () => {
    if (isSubmittingRef.current) return;
    if (!selectedKey?.value) {
      addToast({
        type: "error",
        title: intl.get("pix.receive.selectKeyTitle"),
        description: intl.get("pix.receive.selectKeyDescription"),
      });
      setStep(0);
      return;
    }
    if (!isStatic && !dueDate) {
      addToast({
        type: "error",
        title: intl.get("pix.receive.dueDateRequiredTitle"),
        description: intl.get("pix.receive.dueDateRequiredDescription"),
      });
      setStep(0);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const amountLabel = formatCurrency(linkValue);

    try {
      const result = await progress.runWithProgress(
        () =>
          createPixQrCharge({
            amount: linkValue,
            address_key: selectedKey.value,
            due_date: isStatic ? undefined : dueDate,
            reusable: isStatic ? false : reusable,
            qr_type: qrType,
          }),
        {
          loading: intl.get("pix.receive.generating"),
          loadingDescription: intl.get("pix.receive.generatingDescription", {
            amount: amountLabel,
          }),
          success: intl.get("pix.receive.generated"),
          successDescription: intl.get("pix.receive.generatedDescription"),
          error: intl.get("pix.receive.generateError"),
          errorDescription: intl.get("pix.receive.generateErrorDescription"),
        }
      );

      if (!result?.success) {
        return;
      }

      const chargeData = result?.data ?? { qrcode: "", amount: linkValue, type: qrType };
      setQrState(chargeData);

      const message = `Para me transferir um valor através de sua conta, copie o código pix abaixo e o insira em pix copia e cola na área pix em seu App.\n\n*${chargeData.qrcode}*`;
      setWhatsAppUrl(encodeURI(`https://api.whatsapp.com/send?&text=${message}`));
      setStep(2);
    } catch {
      // Error feedback handled by progress overlay.
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [addToast, dueDate, isStatic, linkValue, progress, qrType, reusable, selectedKey]);

  const handleBackToValue = useCallback(() => {
    setStep(0);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      goBack: handleBackToValue,
      confirm: handleConfirmQrCodeCreation,
      reset: resetStates,
    }),
    [handleBackToValue, handleConfirmQrCodeCreation, resetStates]
  );

  const handleSubmitValue = useCallback(
    async (data) => {
      if (!selectedKey?.value) {
        addToast({
          type: "error",
          title: intl.get("pix.receive.selectKeyTitle"),
          description: intl.get("pix.receive.selectKeyDescription"),
        });
        return;
      }

      const amount = convertToCents(data.value);
      data.value = stringToDecimal(data.value);

      const schema = Yup.object().shape({
        value: isStatic
          ? Yup.number()
              .typeError(intl.get("pix.receive.title"))
              .min(0, "Valor não pode ser negativo")
          : Yup.number()
              .typeError("Digite o valor da cobrança")
              .required("Digite o valor da cobrança")
              .moreThan(0, "Valor deve ser maior que R$ 0,00"),
        due_date: isStatic
          ? Yup.string().nullable()
          : Yup.string()
              .required("Informe a data de validade")
              .matches(/^\d{4}-\d{2}-\d{2}$/, "Data inválida"),
      });
      try {
        await schema.validate(
          { value: data.value ?? 0, due_date: isStatic ? null : dueDate },
          { abortEarly: false }
        );
        setLinkValue(amount);
        setStep(1);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          if (errors.due_date) {
            addToast({
              type: "error",
              title: intl.get("pix.receive.dueDateRequiredTitle"),
              description: errors.due_date,
            });
          }
        }
      }
    },
    [addToast, dueDate, isStatic, selectedKey]
  );

  const qrTypeLabel =
    qrType === QR_TYPES.STATIC
      ? intl.get("pix.receive.qrStatic")
      : intl.get("pix.receive.qrDynamic");

  const confirmationText =
    linkValue > 0
      ? intl.get("pix.receive.confirmQuestion", { amount: formatCurrency(linkValue) })
      : intl.get("pix.receive.confirmQuestionOpen");

  const selectedKeyLabel = selectedKey
    ? `${getPixKeyTypeMeta(selectedKey.type)?.label || "Chave Pix"} · ${formatPixKeyValue(
        selectedKey.type,
        selectedKey.value
      )}`
    : "—";

  const panelPad = footerInModal ? "p-4" : "p-5 md:p-6";
  const formGap = footerInModal ? "space-y-3.5" : "mt-6 space-y-5";

  return (
    <div className="min-w-0 w-full">
      {step < 2 && (
        <PixQrReceiveStepper currentStep={step} compact={footerInModal} />
      )}

      {step === 0 && (
        <div className={`pix-qr-receive-panel ${panelPad}`}>
          {!footerInModal && (
            <>
              <h2 className="text-base font-bold text-brand-dark">{intl.get("pix.receive.title")}</h2>
              <p className="mt-1 text-sm text-gray-500">
                {isStatic
                  ? intl.get("pix.receive.subtitleStatic")
                  : intl.get("pix.receive.subtitle")}
              </p>
            </>
          )}

          <Form
            id={formId}
            type="submit"
            onSubmit={handleSubmitValue}
            ref={formRef}
            className={formGap}
          >
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-brand-dark">
                  {intl.get("pix.receive.keyLabel")}
                </span>
                {!keysLoading && selectableKeys.length > 0 && (
                  <span className="text-[11px] text-gray-400">
                    {intl.get("pix.receive.keyRequired")}
                  </span>
                )}
              </div>

              {keysLoading ? (
                <div className="flex items-center justify-center gap-2 rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-5 text-sm text-gray-500">
                  <CircularProgress size={18} />
                  {intl.get("pix.receive.keyLoading")}
                </div>
              ) : keysError ? (
                <div className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-900">
                  <p className="font-semibold">{keysError.title || "Erro ao carregar chaves"}</p>
                  <p className="mt-1 text-red-800/90">
                    {keysError.description || "Não foi possível listar as chaves Pix da conta."}
                  </p>
                  <button
                    type="button"
                    className="mt-2 inline-flex text-sm font-semibold text-red-800 underline-offset-2 hover:underline"
                    onClick={() => reloadKeys()}
                  >
                    {intl.get("pix.receive.keyErrorRetry")}
                  </button>
                </div>
              ) : selectableKeys.length === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
                  <p className="font-semibold">{intl.get("pix.receive.keyEmptyTitle")}</p>
                  <p className="mt-1 text-amber-800/90">
                    {intl.get("pix.receive.keyEmptyDescription")}
                  </p>
                  <Link
                    to="/pix/keys"
                    className="mt-2 inline-flex text-sm font-semibold text-[#a66f1d] underline-offset-2 hover:underline"
                  >
                    {intl.get("pix.receive.keyEmptyLink")}
                  </Link>
                </div>
              ) : (
                <PixKeySelect
                  options={selectableKeys}
                  value={selectedKeyId}
                  onChange={setSelectedKeyId}
                  compact
                  placeholder={intl.get("pix.receive.keyPlaceholder")}
                  aria-label={intl.get("pix.receive.keyLabel")}
                />
              )}
            </div>

            <div>
              <span className="mb-1.5 block text-sm font-semibold text-brand-dark">
                {intl.get("pix.receive.qrTypeLabel")}
              </span>
              <div
                className="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label={intl.get("pix.receive.qrTypeLabel")}
              >
                <QrTypeOption
                  value={QR_TYPES.DYNAMIC}
                  selected={qrType === QR_TYPES.DYNAMIC}
                  onSelect={setQrType}
                  title={intl.get("pix.receive.qrDynamic")}
                  hint={intl.get("pix.receive.qrDynamicHint")}
                  icon={TbRefresh}
                />
                <QrTypeOption
                  value={QR_TYPES.STATIC}
                  selected={qrType === QR_TYPES.STATIC}
                  onSelect={setQrType}
                  title={intl.get("pix.receive.qrStatic")}
                  hint={intl.get("pix.receive.qrStaticHint")}
                  icon={TbQrcode}
                />
              </div>
            </div>

            <div className="pix-qr-receive-amount-panel rounded-xl border border-gray-100 bg-white p-3 sm:p-3.5">
              <InputMoney />
              {isStatic && (
                <p className="mt-1 text-[11px] text-gray-400">{intl.get("pix.receive.amountHint")}</p>
              )}
            </div>

            {!isStatic && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-brand-dark">
                    {intl.get("pix.receive.dueDate")}
                  </span>
                  <input
                    type="date"
                    name="due_date"
                    value={dueDate}
                    min={tomorrowIsoDate()}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none focus:border-[#e7a73f]"
                  />
                  <span className="mt-0.5 block text-[11px] text-gray-400">
                    {intl.get("pix.receive.dueDateHint")}
                  </span>
                </label>

                <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 sm:mt-[1.625rem] sm:self-start sm:min-h-10">
                  <div className="min-w-0">
                    <span className="block text-sm font-semibold leading-tight text-brand-dark">
                      {intl.get("pix.receive.reusable")}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-gray-400 sm:hidden">
                      {intl.get("pix.receive.reusableHint")}
                    </span>
                  </div>
                  <Switch
                    checked={reusable}
                    onChange={(_, checked) => setReusable(checked)}
                    color="primary"
                    size="small"
                    inputProps={{ "aria-label": intl.get("pix.receive.reusable") }}
                  />
                </div>
              </div>
            )}

            {!footerInModal && (
              <button type="submit" className="btn-primary w-full" disabled={!canContinue}>
                {intl.get("pix.receive.continue")}
              </button>
            )}
          </Form>
        </div>
      )}

      {step === 1 && (
        <div className={`pix-qr-receive-panel ${panelPad}`}>
          <h2 className="text-base font-bold text-brand-dark">
            {intl.get("pix.receive.confirmTitle")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{intl.get("pix.receive.confirmSubtitle")}</p>

          <div className={`${footerInModal ? "mt-4" : "mt-6"} space-y-2.5`}>
            <div className="pix-qr-receive-inset rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
              <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                {intl.get("pix.receive.keyLabel")}
              </span>
              <span className="mt-1 block text-sm font-semibold text-brand-dark">
                {selectedKeyLabel}
              </span>
            </div>

            <div className="grid gap-2.5 sm:grid-cols-2">
              <div className="pix-qr-receive-inset rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
                <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  {intl.get("pix.receive.qrType")}
                </span>
                <span className="mt-1 block text-sm font-semibold text-brand-dark">{qrTypeLabel}</span>
              </div>

              <div className="pix-qr-receive-inset rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3 text-center sm:text-left">
                <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                  Valor da cobrança
                </span>
                <span className="mt-1 block text-xl font-bold text-brand-dark">
                  {linkValue > 0 ? formatCurrency(linkValue) : "—"}
                </span>
              </div>
            </div>

            {!isStatic && (
              <div className="grid gap-2.5 sm:grid-cols-2">
                <div className="pix-qr-receive-inset rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
                  <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    {intl.get("pix.receive.dueDate")}
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-brand-dark">
                    {formatIsoDatePtBr(dueDate)}
                  </span>
                </div>
                <div className="pix-qr-receive-inset rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3">
                  <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                    {intl.get("pix.receive.reusable")}
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-brand-dark">
                    {reusable ? intl.get("pix.receive.yes") : intl.get("pix.receive.no")}
                  </span>
                </div>
              </div>
            )}
          </div>

          <p className="mt-4 text-sm text-gray-600">{confirmationText}</p>

          {!footerInModal && (
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" className="btn-outline w-full sm:w-auto" onClick={handleBackToValue}>
                {intl.get("pix.receive.back")}
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={handleConfirmQrCodeCreation}
                disabled={isSubmitting || !selectedKey?.value}
              >
                {intl.get("pix.receive.confirm")}
              </button>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="pix-qr-receive-panel overflow-hidden">
          <div className="pix-qr-receive-panel__header border-b border-gray-100 px-5 py-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-bold text-brand-dark">
                {intl.get("pix.receive.successTitle")}
              </h2>
              <span className="pix-qr-receive-status-badge inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                <TbCheck className="h-3.5 w-3.5" />
                {intl.get("pix.receive.successActive")}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {qrState?.amount > 0
                ? intl.get("pix.receive.successDescription", {
                    amount: formatCurrency(qrState.amount),
                  })
                : intl.get("pix.receive.successDescriptionOpen")}
            </p>
          </div>

          <div className="grid gap-6 p-5 md:grid-cols-[280px_1fr] md:p-6">
            {(qrState?.image_base64 || qrState?.qrcode) && (
              <div className="pix-qr-receive-qr-frame flex items-center justify-center rounded-2xl border border-gray-100 bg-white p-5">
                {qrState?.image_base64 ? (
                  <img
                    src={
                      qrState.image_base64.startsWith("data:")
                        ? qrState.image_base64
                        : `data:image/png;base64,${qrState.image_base64}`
                    }
                    alt="QR Code Pix"
                    width={200}
                    height={200}
                    className="h-[200px] w-[200px] object-contain"
                  />
                ) : (
                  <QRCode value={qrState.qrcode} size={200} />
                )}
              </div>
            )}

            <div className="flex min-w-0 flex-col gap-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-gray-500">
                  {intl.get("pix.receive.copyPaste")}
                </span>
                <textarea
                  ref={linkRef}
                  value={qrState?.qrcode || ""}
                  readOnly
                  className="pix-qr-receive-code-input min-h-[120px] w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 font-mono text-xs leading-relaxed text-brand-dark outline-none focus:border-[#e7a73f]"
                />
              </label>

              <div className="flex flex-col gap-2 sm:flex-row">
                <button type="button" className="btn-primary flex-1" onClick={copyToClipboard}>
                  <TbCopy className="h-4 w-4" />
                  {intl.get("pix.receive.copyCode")}
                </button>
                <button
                  type="button"
                  className="pix-qr-receive-whatsapp-btn inline-flex h-9 min-h-9 flex-1 items-center justify-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-4 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
                  onClick={() => window.open(whatsAppUrl, "_blank")}
                >
                  <RiWhatsappLine className="h-4 w-4" />
                  {intl.get("pix.receive.sendWhatsApp")}
                </button>
              </div>

              {!footerInModal && (
                <button
                  type="button"
                  className="btn-outline w-full sm:w-auto"
                  onClick={resetStates}
                >
                  {intl.get("pix.receive.newCharge")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
